import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { takeUntil, switchMap } from 'rxjs/operators';
import { from, Subject } from 'rxjs';
import get from 'lodash-es/get';

import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { BREAKPOINTS, ViewportDetection } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { AccordionData, AccordionEvent, ACCORDION_EVENT } from '@finder/shared/components/accordion/accordion.interface';
import { MapDataLocationMarker, MapInterface } from '@finder/shared/components/map/map/map.interface';
import { MAP_DEFAULT_ID } from '@finder/shared/components/map/defaults/map.defaults.constants';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';

@Component({
    selector: 'finder-guest-service-map',
    templateUrl: './guest-service-map.component.html',
    styleUrls: ['./guest-service-map.component.scss']
})
export class GuestServiceMapComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() guestServicesData: any;
    @Input() mapData: any;

    @ViewChild('finderMap', { read: ViewContainerRef }) private finderMap: ViewContainerRef;

    headerData: any = {};
    accordionData: Array<AccordionData>;
    openCategory: string;
    selectedItem: Element;
    showList: boolean;

    private gpsData: any = {};
    private mapObj: MapInterface;
    private accordionEvent: AccordionEvent;
    private destroy$ = new Subject<void>();

    constructor(
        private lazyLoader: LazyLoaderService,
        private viewport: ViewportDetection,
        private winRef: WindowRef,
        private elemRef: ElementRef,
    ) { }

    ngOnInit() {
        this.configureHeader();
        this.configureAccordion();
        this.showList = !this.viewport.isMobile();
    }

    // Ignore the if just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        /**
         * Lazyload the map. It returns a promise. Switch that out for an observer.
         * Once the observer has resolved the promise we switch it out for the
         * map observer. Then we subscribe to the map and capture the object
         * https://stackoverflow.com/a/50799101
         */
        from(this.lazyLoader.lazyLoadModule('finder-map', this.finderMap, {
            defaults: get(this.mapData, 'location.defaults'),
            // Allow the arrow keys to move the map
            markerOptions: { keyboard: true }
        })).pipe(
            // Switch out the observer from the lazy load response with the map is ready observer
            switchMap((data: any) => data.instance.mapIsReady),
            takeUntil(this.destroy$)
        ).subscribe((mapObj: MapInterface) => {
            // Store the map object
            this.mapObj = mapObj;
            // Check to see if we need to process some data
            /* istanbul ignore else */
            if (this.accordionEvent) {
                // Process the data
                this.updateMap(this.accordionEvent);
                // We're done. Clear it out
                this.accordionEvent = null;
            } else if (!this.openCategory) {
                // Display all markes as initial state.
                this.showAllMarkers();
            }
            // We target the canvas element inside the map container to set the aria label
            document.querySelector(`.${MAP_DEFAULT_ID} canvas`)?.
                setAttribute('aria-label', `${this.mapData?.ariaLabelText} ${this.guestServicesData?.title}`);
        });

        this.viewport.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.showList = !this.viewport.isMobile();
            });
    }

    getWindowRef(): Window {
        return this.winRef.nativeWindow;
    }

    getElementRef(): Element {
        return this.elemRef.nativeElement;
    }

    updateMap(accordionEvent: AccordionEvent) {
        // We need to check to see if the map is ready. If not, cache the result and run when the map is ready
        if (!this.mapObj) {
            this.accordionEvent = accordionEvent;

            return;
        }

        // Collect the pins from the event
        switch (accordionEvent.type) {
            case ACCORDION_EVENT.CATEGORY:
                this.updateMarkers(this.gpsData[accordionEvent.id]);
                break;
            case ACCORDION_EVENT.LIST_ITEM:
                // Focus on the pin and open popup
                this.mapObj.jumpToMarker(get(this.gpsData, accordionEvent.id), true);
                this.setSelectedLabelState(accordionEvent.target);
                break;
            case ACCORDION_EVENT.DISPLAY_ALL:
                this.showAllMarkers();
                break;
            default:
                break;
        }
    }

    /**
     * Display all the avaiable markers on the map.
     */
    private showAllMarkers() {
        const markers = {};
        // Get all the markers from the gps object.
        Object.keys(this.gpsData).forEach(park => Object.assign(markers, this.gpsData[park]));
        this.updateMarkers(markers);
    }

    /**
     * Update the selected item in the accordion.
     */
    private setSelectedLabelState(elementToHighlight) {
        // first, remove selected class from the currently selected element, if any
        if (this.selectedItem?.className.includes('selected')) {
            this.selectedItem.classList.remove('selected');
        }
        // set selected item to the newly-clicked element...
        this.selectedItem = elementToHighlight;

        // ... and add the class to style it
        this.selectedItem.classList.add('selected');
    }

    /**
     * Update the map with the given markers.
     *
     * @param gpsData
     */
    private updateMarkers(gpsData) {
        const markersOptions = {
            showPopup: true,
            allowMarkerMouseOver: true,
            allowClustering: true
        };
        // Get the markers from the gps object
        const markersArray: MapDataLocationMarker[] = Object.values(gpsData);
        // Set the markers on the map
        this.mapObj.setMarkers(markersArray, markersOptions);
        // Focus on the pins
        this.mapObj.focusOnPins();
    }

    private configureHeader() {
        // Make sure we have data to format
        if (!this.guestServicesData) {
            return;
        }

        // Configure the header
        this.headerData = {
            titleAlignment: 'left',
            title: this.guestServicesData.title
        };
    }

    private configureAccordion() {
        // Make sure we have data to format
        if (!this.guestServicesData) {
            return;
        }

        // Transform the data into a format the accordion will accept
        this.accordionData = this.guestServicesData.parks.map(park => {
            // If there is only one park, it should be expanded as default.
            if (!this.openCategory && this.guestServicesData.parks.length === 1) {
                this.openCategory = park.id;

                // Create an accordion event to focus the opened category when there's only one
                // See https://myjira.disney.com/browse/GIT-38746
                this.accordionEvent = {
                    type: ACCORDION_EVENT.CATEGORY,
                    id: this.openCategory
                };
            }

            // Setup the base requirements for the accordion
            const result: AccordionData = {
                id: park.id,
                icons: get(this.guestServicesData, 'icons'),
                labels: park
            };

            // Pull all the gps data from the items
            this.gpsData[park.id] = {};

            // Format the list items
            result.listItems = park.locations.map((location: MapDataLocationMarker) => {
                // Store the gps data
                this.gpsData[park.id][location.id] = location;

                return {
                    id: `${park.id}.${location.id}`,
                    name: location.name,
                    icon: location.icon
                };
            });

            return result;
        });
    }
}
